import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  fetchAdminProductCategories,
  fetchAdminProductCategory,
} from '@/api/(admin)/product-categories';
import type { AdminProductCategory } from '@/api/(admin)/product-categories/types';
import type { CategoriesQueryInput } from '../-types';
import { buildCategoriesQs, toCategoriesParams } from '../-utils/listParams';

/** Prefix for every categories cache entry. */
export const adminProductCategoriesQueryKey = [
  'admin',
  'product-categories',
] as const;

/** Shared by the route loader and the page, so the prefetch warms what the page reads. */
export function adminProductCategoriesQueryOptions(
  input: CategoriesQueryInput,
) {
  const params = toCategoriesParams(input);
  return queryOptions({
    queryKey: [...adminProductCategoriesQueryKey, 'list', params] as const,
    queryFn: () => fetchAdminProductCategories(buildCategoriesQs(params)),
  });
}

export function useAdminProductCategories(input: CategoriesQueryInput) {
  const query = useQuery(adminProductCategoriesQueryOptions(input));
  const rows: AdminProductCategory[] = query.data?.data ?? [];
  return {
    ...query,
    rows,
    totalCount: query.data?.metadata.pagination.total ?? 0,
  };
}

/** One category by id. */
export function adminProductCategoryQueryOptions(id: string) {
  return queryOptions({
    queryKey: [...adminProductCategoriesQueryKey, 'detail', id] as const,
    queryFn: async () => (await fetchAdminProductCategory(id)).data,
  });
}

/**
 * The category being drilled into, so the list can name it.
 *
 * Fetched rather than read off the rows: the parent is by definition not in
 * the list showing its children, and the page it came from may be gone (a
 * refresh, or a shared link).
 */
export function useAdminParentCategory(parentId: string | null) {
  const query = useQuery({
    ...adminProductCategoryQueryOptions(parentId ?? ''),
    enabled: parentId !== null,
  });
  return parentId === null ? undefined : query.data;
}
